import { useQuery } from '@tanstack/react-query';
import { handleApiResponse } from '@/functions';
import { useInvalidateQueries } from '@/hooks';
import { apiClient } from '@/components/api';
import { Paginated } from '@/lib/interface';
import routes from '@/lib/routes';
import { CoinPackageNameEnum, PaymentPlatformEnum } from '@/enums';

/**
 * Payment record type matching the API response structure.
 */
export interface IUserPayment {
    _id: string;
    createdAt: string;
    packageName: CoinPackageNameEnum;
    priceInEur: number;
    price: number;
    currency: string;
    provider: PaymentPlatformEnum;
    transactionId: string;
}

/**
 * Query parameters for fetching user payments
 */
export interface IGetUserPaymentsParams {
    userId: string;
    page?: number;
    pageSize?: number;
    provider?: PaymentPlatformEnum;
    packageName?: CoinPackageNameEnum;
    startDate?: string;
    endDate?: string;
    minPrice?: number;
    maxPrice?: number;
    sortBy?: 'createdAt' | 'priceInEur' | 'packageName' | 'provider';
    sortOrder?: 'asc' | 'desc';
}

type TResponseData = Paginated<IUserPayment>;

/**
 * Hook for fetching paginated user payments from the admin endpoint.
 * 
 * @param params - Query parameters including userId and optional filters
 * @param run - Whether to execute the query (default: true)
 * @returns Payment data, loading state, and helper functions
 */
export const useRequestGetUserPayments = (params: IGetUserPaymentsParams, run = true) => {
    const { invalidate } = useInvalidateQueries();

    const { isFetching, data, error, dataUpdatedAt } = useQuery({
        queryKey: [routes.api.users.base, 'payments', params, run],
        queryFn: ({ queryKey: [_url, _, _params, _run] }) =>
            !_run || !(_params as IGetUserPaymentsParams)?.userId
                ? Promise.resolve(null)
                : fetchQuery(_params as IGetUserPaymentsParams),
        enabled: run && !!params?.userId,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });

    const fetchQuery = (params: IGetUserPaymentsParams) => {
        // Build query string from params
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.set('page', String(params.page));
        if (params.pageSize) queryParams.set('pageSize', String(params.pageSize));
        if (params.provider) queryParams.set('provider', params.provider);
        if (params.packageName) queryParams.set('packageName', params.packageName);
        if (params.startDate) queryParams.set('startDate', params.startDate);
        if (params.endDate) queryParams.set('endDate', params.endDate);
        if (params.minPrice !== undefined) queryParams.set('minPrice', String(params.minPrice));
        if (params.maxPrice !== undefined) queryParams.set('maxPrice', String(params.maxPrice));
        if (params.sortBy) queryParams.set('sortBy', params.sortBy);
        if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);

        const queryString = queryParams.toString();
        const url = routes.api.users.payments(params.userId) + (queryString ? `?${queryString}` : '');

        return apiClient
            .get<TResponseData>(url)
            .then(handleApiResponse);
    };

    const payments = data?.data ?? [];

    const refresh = () =>
        invalidate(routes.api.users.base, 'payments', params);

    return {
        paymentsUpdatedAt: dataUpdatedAt,
        isLoadingPayments: isFetching,
        payments,
        pagination: data?.pagination,
        refresh,
        error,
        data
    };
};
